import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Textarea } from '@artstorefronts/ui';

import { SAY_HI_OPENER_KEYS, type SayHiOpenerKey } from './sayHiOpeners';

interface SayHiNudgeDialogProps {
  open: boolean;
  displayName: string;
  onOpenChange: (open: boolean) => void;
  onContinue: (body: string) => void;
  onDismiss: () => void;
}

export function SayHiNudgeDialog({ open, displayName, onOpenChange, onContinue, onDismiss }: SayHiNudgeDialogProps) {
  const { t } = useTranslation('social');
  const [message, setMessage] = useState('');
  const [selectedKey, setSelectedKey] = useState<SayHiOpenerKey | null>(SAY_HI_OPENER_KEYS[0]);
  // Continue opens dock compose; suppress Radix focus-return only for that path.
  const suppressCloseAutoFocusRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setMessage('');
      setSelectedKey(SAY_HI_OPENER_KEYS[0]);
      suppressCloseAutoFocusRef.current = false;
      return;
    }
    setSelectedKey(SAY_HI_OPENER_KEYS[0]);
    setMessage(t(`connection.say-hi-opener-${SAY_HI_OPENER_KEYS[0]}`, { name: displayName }));
  }, [open, displayName, t]);

  function pickOpener(key: SayHiOpenerKey) {
    setSelectedKey(key);
    setMessage(t(`connection.say-hi-opener-${key}`, { name: displayName }));
  }

  function handleOpenChange(next: boolean) {
    // Radix close (overlay / Esc / X) — treat as dismiss. Continue path
    // only calls onContinue; parent flips `open` by clearing target.
    if (!next && open) {
      onDismiss();
    }
    onOpenChange(next);
  }

  function handleContinue() {
    const body = message.trim();
    if (!body) return;
    suppressCloseAutoFocusRef.current = true;
    onContinue(body);
  }

  function handleDismiss() {
    suppressCloseAutoFocusRef.current = false;
    onDismiss();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="say-hi-nudge-dialog"
        onCloseAutoFocus={(e) => {
          if (suppressCloseAutoFocusRef.current) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('connection.say-hi-title', { name: displayName })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-gray-500">{t('connection.say-hi-description')}</p>
          <div className="flex flex-col gap-2">
            {SAY_HI_OPENER_KEYS.map((key) => {
              const label = t(`connection.say-hi-opener-${key}`, { name: displayName });
              const selected = selectedKey === key;
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`say-hi-opener-${key}`}
                  aria-pressed={selected}
                  onClick={() => pickOpener(key)}
                  className={`text-left text-sm rounded-lg border px-3 py-2 transition-colors ${
                    selected
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <Textarea
            data-testid="say-hi-nudge-textarea"
            className="min-h-28"
            value={message}
            onChange={(e) => {
              setSelectedKey(null);
              setMessage(e.target.value);
            }}
            placeholder={t('connection.say-hi-placeholder')}
            maxLength={5000}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleDismiss}>
            {t('connection.say-hi-later')}
          </Button>
          <Button
            type="button"
            data-testid="say-hi-nudge-continue"
            disabled={!message.trim()}
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={handleContinue}
          >
            {t('connection.say-hi-continue')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
